<template>
  <main class="container is-fluid">
    <div class="columns">
      <div class="field column is-one-third">
        <div class="has-text-right mb-2 buttons">
          <router-link
            v-if="!selectFunction"
            :to="{ name: 'worker-types-list' }"
            class="button"
          >
            Manage types
          </router-link>
          <CreateForm v-on:worker-created="updateWorkersPage" />
        </div>

        <div
          v-if="!selectFunction"
          class="tabs is-centered mb-2"
        >
          <ul>
            <li
              :class="{ 'is-active': !archivedWorkers }"
              v-on:click="archivedWorkers = false"
            >
              <a>Available workers</a>
            </li>
            <li
              :class="{ 'is-active': archivedWorkers }"
              v-on:click="archivedWorkers = true"
            >
              <a>Archived workers</a>
            </li>
          </ul>
        </div>

        <div class="field has-addons">
          <!-- Selection of a worker is required to list versions -->
          <div class="control">
            <div class="select">
              <select v-model="typeFilter">
                <option value="">Filter by worker type…</option>
                <option
                  v-for="t in workerTypeStore.workerTypes"
                  :key="t.id"
                  :value="t.slug"
                >
                  {{ truncateShort(t.display_name) }}
                </option>
              </select>
            </div>
          </div>
          <div class="control is-expanded">
            <form
              v-if="workersPage"
              class="field"
              v-on:submit.prevent="filter"
            >
              <input
                class="input"
                type="text"
                v-model="nameFilter"
                placeholder="Filter by name…"
              />
            </form>
          </div>
        </div>
        <div class="control">
          <Paginator
            :response="workersPage"
            v-slot="{ results }"
            :loading="loading"
            v-model:page="page"
            singular="worker"
            plural="workers"
          >
            <table class="table is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="worker in results"
                  :key="worker.id"
                  v-on:click="selectedWorkerId = worker.id"
                  class="is-clickable"
                  :class="{ 'is-selected': selectedWorkerId === worker.id }"
                >
                  <td>
                    <WorkerTag :worker="worker" />
                  </td>
                </tr>
              </tbody>
            </table>
          </Paginator>
        </div>
      </div>
      <div class="field column is-two-thirds">
        <WorkerDetails
          v-if="selectedWorkerId"
          :worker-id="selectedWorkerId"
          :select-function="selectFunction"
          :only-select-available="onlySelectAvailable"
          :simple="!!selectFunction"
        />
        <div
          v-else
          class="notification is-info"
        >
          Please select a <strong>worker</strong> on the left panel.
        </div>
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import { isEmpty } from 'lodash'
import { ref, watch } from 'vue'

import type { ListWorkersParameters } from '@/api'
import Paginator from '@/components/Paginator.vue'
import { errorParser } from '@/helpers'
import { truncateShort } from '@/mixins/truncate'
import { useNotificationStore, useWorkerStore, useWorkerTypeStore } from '@/stores'
import type { PageNumberPagination, UUID } from '@/types'
import type { Worker, WorkerVersion } from '@/types/worker'

import CreateForm from './CreateForm.vue'
import WorkerDetails from './Details.vue'
import WorkerTag from './WorkerRuns/WorkerTag.vue'

const { selectFunction, onlySelectAvailable } = defineProps<{
  /**
   * A function called when a WorkerVersion is selected.
   * The button to select a worker version is only shown when this function is set.
   */
  selectFunction?: (version: WorkerVersion) => unknown | Promise<unknown>
  /**
   * When a `selectFunction` is defined, only a WorkerVersion with an `available` state will be selectable.
   */
  onlySelectAvailable?: boolean
}>()

const loading = ref(false)
const workersPage = ref<PageNumberPagination<Worker>>()
const selectedWorkerId = ref<UUID | null>(null)
const page = ref(1)
const nameFilter = ref('')
const typeFilter = ref('')
const archivedWorkers = ref(false)

const notificationStore = useNotificationStore()
const workerStore = useWorkerStore()
const workerTypeStore = useWorkerTypeStore()

async function updateWorkersPage() {
  loading.value = true
  if (isEmpty(workerTypeStore.workerTypes)) await fetchWorkerTypes()
  try {
    const payload: ListWorkersParameters = { page: page.value, archived: archivedWorkers.value }
    if (nameFilter.value) payload.name = nameFilter.value
    if (typeFilter.value) payload.type = typeFilter.value
    workersPage.value = await workerStore.listWorkers(payload)
  } catch (err) {
    notificationStore.notify({
      type: 'error',
      text: `An error occurred listing workers: ${errorParser(err)}`,
    })
  } finally {
    loading.value = false
  }
}

async function filter() {
  // Update workers list. Reset page if required
  if (page.value === 1) return updateWorkersPage()
  page.value = 1
}

async function fetchWorkerTypes() {
  loading.value = true
  try {
    await workerTypeStore.list()
  } catch (err) {
    notificationStore.notify({
      type: 'error',
      text: `An error occurred listing worker types: ${errorParser(err)}`,
    })
  } finally {
    loading.value = false
  }
}

watch(page, updateWorkersPage, { immediate: true })
watch(typeFilter, filter)
watch(archivedWorkers, () => {
  page.value = 1
  updateWorkersPage()
})
</script>

<style lang="scss" scoped>
.container {
  height: 100%;
  > .columns {
    height: inherit;
    > .column {
      overflow: auto;
    }
  }
}
.tabs {
  width: 100%;
}
.tabs a {
  padding-top: 0;
}
</style>
